import type { WorkLogEntry } from "../../session-logic";

export function workEntryCategory(entry: WorkLogEntry) {
  if (entry.tone === "error") return "error";
  if (entry.requestKind && /approval/i.test(entry.label)) return "approval";
  if (entry.requestKind === "file-read") return "file-read";
  if (entry.requestKind === "file-change") return "file-change";
  if (entry.commandAction?.type === "read") return "file-read";
  if (entry.commandAction?.type === "search") return "file-search";
  if (entry.commandAction?.type === "listFiles") return "file-list";
  if (entry.requestKind === "command" || entry.itemType === "command_execution" || entry.command) {
    return "command";
  }
  if (entry.itemType === "file_change" || entry.changedFiles?.length) return "file-change";
  if (entry.itemType === "web_search") return "web-search";
  if (entry.itemType === "image_view") return "image";
  if (entry.itemType === "mcp_tool_call") return "mcp-tool";
  if (entry.itemType === "dynamic_tool_call" || entry.itemType === "collab_agent_tool_call") {
    return "tool";
  }
  if (entry.tone === "thinking") return "thinking";
  return entry.tone === "tool" ? "other-tool" : "info";
}

const workGroupLabels = {
  command: "ran commands",
  "file-read": "read files",
  "file-search": "searched files",
  "file-list": "listed files",
  "file-change": "edited files",
  "web-search": "searched the web",
  image: "viewed images",
  "mcp-tool": "called tools",
  tool: "called tools",
  "other-tool": "called tools",
  thinking: "thought through the task",
  error: "reported errors",
  approval: "approval updates",
  info: "updated progress",
} as const;

export function commandActionLabel(entry: WorkLogEntry): string | null {
  const action = entry.commandAction;
  if (!action) return null;
  const started = entry.label === "Command started";
  switch (action.type) {
    case "read":
      return `${started ? "Reading" : "Read"} ${action.name}`;
    case "search": {
      const query = action.query ? ` for ${action.query}` : "";
      const path = action.path ? ` in ${action.path}` : "";
      return `${started ? "Searching" : "Searched"}${query}${path || (query ? "" : " files")}`;
    }
    case "listFiles":
      return `${started ? "Listing" : "Listed"} ${action.path || "files"}`;
    case "unknown":
      return `${started ? "Started" : "Ran"} ${action.command}`;
  }
}

const DYNAMIC_TOOL_LABELS: Record<
  string,
  Record<NonNullable<WorkLogEntry["toolStatus"]>, string>
> = {
  dashboard_generate: {
    running: "Building a dashboard, usually 1 to 3 minutes",
    completed: "Built a dashboard",
    failed: "Could not build the dashboard",
  },
  dashboard_artifact_get: {
    running: "Reading a dashboard",
    completed: "Read a dashboard",
    failed: "Could not read the dashboard",
  },
};

/** Plain-language progress for a control-plane dynamic tool, else null. */
export function dynamicToolLabel(entry: Pick<WorkLogEntry, "toolName" | "toolStatus">) {
  if (!entry.toolName || !entry.toolStatus) return null;
  const known = DYNAMIC_TOOL_LABELS[entry.toolName];
  if (known) return known[entry.toolStatus];
  const verb = { running: "Running", completed: "Ran", failed: "Failed to run" }[entry.toolStatus];
  return `${verb} ${entry.toolName}`;
}

export function workGroupLabel(entries: readonly WorkLogEntry[]): string {
  const last = entries.at(-1);
  // A long tool still running is what the person is waiting on, even when collapsed.
  if (last?.toolStatus === "running") return dynamicToolLabel(last) ?? "Activity";
  const toolLabels = entries.map(dynamicToolLabel);
  if (toolLabels.every((label) => label !== null)) return [...new Set(toolLabels)].join(", ");
  const labels = [...new Set(entries.map((entry) => workGroupLabels[workEntryCategory(entry)]))];
  const label = labels.join(", ") || "Activity";
  return label.charAt(0).toUpperCase() + label.slice(1);
}
